
import * as THREE from "three";
import React, { useState, useEffect, memo } from "react";
import { Spline, MakeSplinePoints, TubeCurve } from "../shape/Spline";
import { getMiddlePointsOnSpline } from "../shape/utils";
import { PointsDrag3D } from "../shape/PointDrag3D";
import { PointsClick } from "../shape/PointClick";
import { Points } from "../shape/Point";
import { DIV_LINE, DIV_CLICK } from '../store/global';
import { findClosestPointOnPoints } from "../shape/utils";
import { convertPointsFromNonIndexToIndex } from "../shape/utils/points";

/**
 * Renders a component that displays 3D points and allows interaction with them.
 *
 * @component
 * @param {Array} vpoints - An array of 3D points.
 * @param {string} colorSpline - The color of the spline.
 * @param {string} colorDrag - The color of the draggable points.
 * @param {string} colorDisable - The color of the disabled points.
 * @param {string} colorClick - The color of the clickable points.
 * @param {Object} mesh - The mesh object.
 * @param {number} mode - The mode of the component. 0: Add, 1: Delete.
 * @param {number} angle - The angle of the view. 1: Front, 2: Back, 3: Top, 4: Bottom, 5: Right, 6: Left.
 * @param {Function} callback - The callback function to be called when the position of a point is updated.
 * @returns {JSX.Element} The rendered component.
 */
export const MPoints3D = memo(
  ({vpoints, 
    colorSpline, 
    colorDrag, 
    colorDisable, 
    colorClick, 
    mesh, 
    mode, 
    angle, 
    callback 
  }) => {

  if(!vpoints) return null;
  if(vpoints.length == 0) return null;
  vpoints.forEach( pt => {
    if (pt == null) return null;
  });

  var [lvpoints, setVpoints]      = useState();
  var [ptsDrag, setPtsDrag]       = useState([]); //Drag possible points
  var [idxDrag, setIdxDrag]       = useState([]); //Index index of drag possible points
  var [ptsClick, setPtsClick]     = useState([]); //Click possible points
  var [ptsDisable, setPtsDisable] = useState([]); //Drag impossible points

  // find closest points on socket and set it to vpointsCurve
  // const vpointsCurve = new THREE.CatmullRomCurve3(vpoints).getPoints(100);
  // const sourcePoints = convertPointsFromNonIndexToIndex(mesh.geometry.attributes.position.array);
  // let vpointsCurveOnMesh = vpointsCurve.map((p) => {
  //   const closestPoint = findClosestPointOnPoints(sourcePoints, p);
  //   return closestPoint.point;
  // });
  // vpointsCurveOnMesh = new THREE.CatmullRomCurve3(vpointsCurveOnMesh).getPoints(20);


  useEffect(() => {
    //vpoints.push(vpoints[0])
    setVpoints(vpoints);
  }, [vpoints]);

  useEffect(() => {
    const tmp1 = getMiddlePointsOnSpline(vpoints, true); // this is clickable points to add control points

    setPtsClick(tmp1);

    // Segregate vpoints into drag possible points (ptsDrag) and drag impossible points (ptsDisable)
    const [tmp2, tmp3, tmp4] = getPtsDrag(vpoints, angle);
    //console.log("tmp2", tmp2);
    setPtsDrag(tmp2);
    setPtsDisable(tmp3);
    setIdxDrag(tmp4);
  }, [vpoints]);

  /**
   * Returns an array of points that can be dragged along with their indices, and an array of points that cannot be dragged based on the given angle (viewpoint).
   * @param {Array} vpoints - An array of 3D points.
   * @param {number} angle - An integer representing the angle of the view. 1: Front, 2: Back, 3: Top, 4: Bottom, 5: Right, 6: Left.
   * @returns {Array} An array containing three arrays: ptsDrag (points that can be dragged), ptsDisable (points that cannot be dragged), and idxDrag (indices of points that can be dragged).
   */
  function getPtsDrag(vpoints, angle){
    var ptsDrag = [];
    var ptsDisable = [];
    var idxDrag = [];

    vpoints.forEach((p, i) => {
      switch(angle){
        case 1: //Front
          if(p.z > 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        case 2: //Back
          if(p.z < 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        case 3: //Top
          if(p.y > 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        case 4: //Bottom
          if(p.y < 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        case 5: //Right
          if(p.x < 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        case 6: //Left
          if(p.x > 0) {
            ptsDrag.push(p);
            idxDrag.push(i);
          }
          else ptsDisable.push(p);
          break;
        default:
          ptsDisable.push(p);
          break;
      }
    }); 
    return [ptsDrag, ptsDisable, idxDrag];
  }

  /**
   * Calls the callback function with updated nodes after updating the position of a node.
   *
   * @param {string} uuid - The unique identifier of the node to be updated.
   * @param {Object} position - The new position of the node.
   */
  const callEnd = (uuid, position) => {
    var newnodes = vpoints.slice();
    newnodes[uuid] = position;
    setVpoints(newnodes);
    callback(newnodes);
  };

  /**
   * Calls the add function to add a new point to the list of 3D points.
   * @param {number} uuid - The unique identifier of the point to be added.
   * @returns {void}
   */
  const callAdd = (uuid) => {
    var newnodes = lvpoints.slice();
    newnodes.splice(uuid+1, 0, ptsClick[uuid]);
    
    setVpoints(newnodes);
    callback(newnodes);
  }

  /**
   * Deletes a node from the list of 3D points and calls the callback function with the updated list.
   * @param {number} uuid - The unique identifier of the node to be deleted.
   * @returns {void}
   */
  const callDel = (uuid) => {
    console.log("-----callDel-----", uuid);
    if(lvpoints.length < 5) {
      console.log("Can't delete node because only 4 lefts.");
      return;
    }
    var newnodes = lvpoints.slice();
    newnodes.splice(uuid, 1);
    setVpoints(newnodes);  
    callback(newnodes);
  }
  
  return (
    <>
      {/* { vpoints ? <Spline vpoints={lvpoints} color={colorSpline} lineWidth={5} close={true} div={DIV_LINE}/> : null} */}
      { vpoints ? <TubeCurve vpoints={vpoints} color={colorSpline} nPoint={100} radius={0.3} close={true} depthTest={true} /> : null}
      {(mode == 0) // Add
        ? <>
            <PointsDrag3D vpoints={ptsDrag} idx={idxDrag} color={colorDrag} size={1} callEnd={callEnd} mesh={mesh}/>
            <PointsClick vpoints={ptsClick} color={colorClick} size={1} callback={callAdd}/>
            <Points vpoints={ptsDisable} color={colorDisable} size={1} />
          </>
        : null}
      {(mode == 1) // Dell
        ? <PointsClick vpoints={lvpoints} color={colorDrag} size={1} callback={callDel}/>
        : null}
    </>
  );
});
export default MPoints3D;